import { IGlobalRangesInfoResponse, ILPDataResponse, IRangeParamsResponse } from "@src/entities/Range";
import { IHistory } from "@src/utils/types";
import { getBackendApiUrl } from "@src/constants/api";
import axios from "axios";

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

class SimpleCache {
  private cache = new Map<string, CacheEntry<any>>();

  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;
    
    const now = Date.now();
    if (now - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      return null;
    }
    
    return entry.data;
  }

  set<T>(key: string, data: T, ttl: number = 10000): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl
    });
  }

  clear(): void {
    this.cache.clear();
  }
}

const cache = new SimpleCache();

export interface IGetRanges {
  page: number;
  size: number;
  sortBy?: "fact_liquidity" | "earned" | "virtual_liquidity" | "apr";
  order?: "asc" | "desc";
  search?: string;
  userAddress?: string;
  minLiquidity?: number;
  timeRange?: "all" | "1d" | "7d" | "30d" | "90d" | "1y";
}

export interface IGetGlobalRangesInfo {
  minLiquidity: number;
  userAddress?: string;
  active?: boolean;
}

export interface IGetRange {
  startTime?: number;
  endTime?: number;
  nominatePriceIn?: string;
  user?: string;
  charts?: boolean;
  timeRange?: "all" | "1d" | "7d" | "30d" | "90d" | "1y";
}

export interface IGetRangesResponse {
  ranges: IRangeParamsResponse[];
  totalItems: number;
}

export interface IGetChartData {
  startTime?: number;
  endTime?: number;
  nominatePriceIn?: string;
}

export interface IStakingStatistics {
  asset_id: string;
  name: string;
  group: "common" | "index";
  apr_1d: number;
  apr_7d: number;
  apr_30d: number;
  apr_1y: number;
}

export interface IProvidedAssetResponse {
  asset_id: string;
  name: string;
  leverage: number;
  earned_amount: number;
  earned_amount_usd: number;
  provided_amount: number;
  provided_amount_usd: number;
}

export interface IProvidedResponse {
  provider_address: string;
  pool_address: string;
  pool_mode: string;
  index_staked: number;
  share: number;
  provided_usd: number;
  claimed_usd: number;
  unclaimed_usd: number;
  lp_token_id?: string;
  lp_token_price: number;
  lp_token_market_price: number;
  lp_token_name?: string;
  lp_token_domain: string;
  assets_data: IProvidedAssetResponse[];
}

const rangesService = {
  getRanges: async (params: IGetRanges): Promise<IGetRangesResponse> => {
    const paramsString = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      value !== undefined && paramsString.append(key, value.toString());
    });
    const baseUrl = `${getBackendApiUrl()}/stats/v1/statistics/pools/ranged`;
    const url = `${baseUrl}?${paramsString.toString()}`;
    const { data } = await axios.get(url);
    return { ranges: data.pools, totalItems: data.total };
  },
  getGlobalRangesInfo: async (params: IGetGlobalRangesInfo): Promise<IGlobalRangesInfoResponse> => {
    const baseUrl = `${getBackendApiUrl()}/stats/v1/statistics/pools/global_info`;
    const paramsString = new URLSearchParams({
      poolMode: "ranged",
    });
    Object.entries(params).forEach(([key, value]) => {
      value !== undefined && paramsString.append(key, value.toString());
    });
    const url = `${baseUrl}?${paramsString.toString()}`;
    const { data } = await axios.get(url);
    return data;
  },
  // Lightweight availability probe for a specific range by address.
  // Returns true when the endpoint responds with 200, false on 500 (not ready yet).
  // Any other network error will be treated as not available for now (retry logic handled by caller).
  pingRange: async (address: string): Promise<boolean> => {
    if (!address || address.trim() === '') {
      return false;
    }
    const baseUrl = `${getBackendApiUrl()}/stats/v1/statistics/pools/ranged`;
    const url = `${baseUrl}/${address.trim()}/data`;
    try {
      const res = await axios.get(url);
      return res.status === 200;
    } catch (e: any) {
      if (e?.response?.status === 500) return false;
      return false;
    }
  },
  getRangeByAddress: async (address: string, params?: IGetRange): Promise<IRangeParamsResponse> => {
    if (!address || address.trim() === '') {
      throw new Error('Range address is required');
    }
    const baseUrl = `${getBackendApiUrl()}/stats/v1/statistics/pools/ranged`;
    const rangeUrl = `${baseUrl}/${address.trim()}/data`;
    const paramsString = params ? `?${new URLSearchParams(params as Record<string, string>).toString()}` : "";
    const url = `${rangeUrl}${paramsString}`;
    const { data } = await axios.get(url);
    return data;
  },
  getLPData: async (address: string, userAddress: string, force?: boolean): Promise<ILPDataResponse> => {
    if (!address || address.trim() === '') {
      throw new Error('Range address is required');
    }
    const baseUrl = `${getBackendApiUrl()}/stats/v1/statistics/pools/provided_data`;
    const paramsString = new URLSearchParams({
      poolAddress: address.trim(),
      userAddress: userAddress,
      poolMode: "ranged",
      page: "1",
      size: "1",
      force: force ? "true" : "false"
    });
    const url = `${baseUrl}?${paramsString.toString()}`;
    const { data } = await axios.get(url);
    return data.data[0];
  },
  getChartData: async (address: string, params?: IGetChartData): Promise<IHistory[]> => {
    if (!address || address.trim() === '') {
      throw new Error('Range address is required');
    }
    const baseUrl = `${getBackendApiUrl()}/stats/v1/statistics/pools/ranged`;
    const rangeUrl = `${baseUrl}/${address.trim()}/charts`;
    const paramsString = params ? `?${new URLSearchParams(params as Record<string, string>).toString()}` : "";
    const url = `${rangeUrl}${paramsString}`;
    const { data } = await axios.get(url);
    return data.charts;
  },
  getUserTotalProvided: async (userAddress: string): Promise<number> => {
    const cacheKey = `getUserTotalProvided_${userAddress}`;
    const cached = cache.get<number>(cacheKey);
    if (cached !== null) return cached;
    
    const baseUrl = `${getBackendApiUrl()}/stats/v1/statistics/pools/provided_data`;
    const paramsString = new URLSearchParams({
      userAddress: userAddress,
      poolMode: "ranged",
      page: "1",
      size: "500"
    });
    const url = `${baseUrl}?${paramsString.toString()}`;
    const { data } = await axios.get(url);
    
    cache.set(cacheKey, data.total_provided_usd, 5000);
    return data.total_provided_usd;
  },
  getUserInvestments: async (userAddress: string): Promise<IProvidedResponse[]> => {
    const cacheKey = `getUserInvestments_${userAddress}`;
    const cached = cache.get<IProvidedResponse[]>(cacheKey);
    if (cached !== null) return cached;
    
    const baseUrl = `${getBackendApiUrl()}/stats/v1/statistics/pools/provided_data`;
    const paramsString = new URLSearchParams({
      userAddress: userAddress,
      poolMode: "ranged",
      page: "1",
      size: "500"
    });
    const url = `${baseUrl}?${paramsString.toString()}`;
    const { data } = await axios.get(url);
    
    cache.set(cacheKey, data.data, 5000);
    return data.data;
  },
  getStakingStatistics: async (group?: "common" | "index"): Promise<IStakingStatistics[]> => {
    const baseUrl = `${getBackendApiUrl()}/stats/v1/statistics/pools/aprs`;
    const paramsString = new URLSearchParams({
      page: "1",
      size: "500",
      ...(group ? { group } : {})
    });
    const url = `${baseUrl}?${paramsString.toString()}`;
    const { data } = await axios.get(url);
    return data.data;
  }
};

export default rangesService;
